import {
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Stack,
  Textarea,
  useToast,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  HStack,
  Flex,
  Box,
  Text,
  Link,
  Collapse,
  Spinner,
  Code,
  Badge,
  IconButton,
  Tooltip,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { RiKeyLine, RiSettings4Line, RiErrorWarningLine, RiWifiLine, RiRefreshLine } from 'react-icons/ri';
import { MdOutlineSmartToy } from 'react-icons/md';
import { SiGithub } from 'react-icons/si';
import { IoChevronDown, IoChevronUp, IoCheckmarkCircle, IoCloseCircle, IoCopyOutline, IoTimeOutline } from 'react-icons/io5';
import { StyledSelect } from '../helivan/StyledSelect';
import {
  useCreateCustomAgentMutation,
  useUpdateCustomAgentMutation,
  useGenerateConnectorEnrollmentMutation,
  useGetConnectorStatusLazyQuery,
  useGetCustomAgentLazyQuery,
  CustomAgentStatus,
  ConnectionType as AgentConnectionType,
  ConnectorEnrollmentStatus,
  useGetAgentCreationLimitsQuery,
} from '../../generated/graphql';
import { useNavigate } from 'react-router-dom';

// Subtle gradient animation
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

type ConnectionType = 'MOLTBOT' | 'API';

interface CreateAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateAgentModal = ({
  isOpen,
  onClose,
  onSuccess,
}: CreateAgentModalProps) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [createAgent, { loading }] = useCreateCustomAgentMutation();
  const [updateAgent] = useUpdateCustomAgentMutation();
  const [generateEnrollment, { loading: enrollmentLoading }] = useGenerateConnectorEnrollmentMutation();
  const [getConnectorStatus] = useGetConnectorStatusLazyQuery();
  const [getCustomAgent] = useGetCustomAgentLazyQuery();
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Onboarding mode: Automatic (copyable prompt) is the default; Manual is the form.
  const [mode, setMode] = useState<'automatic' | 'manual'>('automatic');
  // Whether the Automatic tab has generated an agent + enrollment prompt yet.
  const [automaticGenerated, setAutomaticGenerated] = useState(false);

  // Moltbot enrollment state
  const [step, setStep] = useState<'form' | 'enrollment'>('form');
  const [createdAgentId, setCreatedAgentId] = useState<string | null>(null);
  const [enrollmentCode, setEnrollmentCode] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [connectorStatus, setConnectorStatus] = useState<string | null>(null);
  const [enrollmentStatus, setEnrollmentStatus] = useState<ConnectorEnrollmentStatus | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch usage limits
  const { data: limitsData } = useGetAgentCreationLimitsQuery({
    skip: !isOpen,
  });
  const usageLimits = limitsData?.user?.usageLimits;
  const canCreateAgent = usageLimits?.canCreateAgent ?? true;
  const currentAgentCount = usageLimits?.currentAgentCount ?? 0;
  const maxAgents = usageLimits?.maxAgents ?? 1;

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Reset form and stop polling when modal closes
  useEffect(() => {
    if (!isOpen) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      // Reset form state for next open
      setFormData({
        connectionType: 'MOLTBOT',
        name: '',
        description: '',
        avatarUrl: '',
        apiEndpoint: '',
        apiAuthType: 'API_KEY',
        apiKey: '',
        systemPrompt: '',
        temperature: 0.7,
        maxTokens: 4000,
      });
      setShowAdvanced(false);
      setConnectionTest({ status: 'idle' });
      setStep('form');
      setMode('automatic');
      setAutomaticGenerated(false);
      setCreatedAgentId(null);
      setEnrollmentCode(null);
      setExpiresAt(null);
      setConnectorStatus(null);
      setEnrollmentStatus(null);
    }
  }, [isOpen]);

  // Poll for connector status
  const startPolling = useCallback((agentId: string) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    const stopPolling = () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };

    const finishConnected = (title: string, description: string) => {
      toast({ title, description, status: 'success', duration: 4000 });
      onSuccess();
      onClose();
    };

    const pollStatus = async () => {
      try {
        const result = await getConnectorStatus({
          variables: { agentId },
          fetchPolicy: 'network-only',
        });

        const status = result.data?.getConnectorStatus;
        if (status) {
          setConnectorStatus(status.status);
          setEnrollmentStatus(status.enrollmentStatus as ConnectorEnrollmentStatus);

          // Connector path: when ONLINE, update agent status + gateway URL, then close.
          if (status.status === 'ONLINE') {
            stopPolling();
            const updateInput: Record<string, any> = { status: CustomAgentStatus.Active };
            const gatewayUrl = formDataRef.current.apiEndpoint.trim();
            if (gatewayUrl) {
              updateInput.apiEndpoint = gatewayUrl;
            }
            await updateAgent({
              variables: { id: agentId, input: updateInput },
            });
            finishConnected('Agent connected', 'Your agent is online and ready to use');
            return;
          }
        }

        // DIRECT path: the agent may instead claim its public endpoint via skill.md
        // (/api/agent/claim), which flips the record to DIRECT + ACTIVE without ever
        // reporting a connector status. Detect that so the wait panel resolves too.
        const agentResult = await getCustomAgent({
          variables: { id: agentId },
          fetchPolicy: 'network-only',
        });
        const agent = agentResult.data?.getCustomAgent;
        if (
          agent &&
          agent.connectionType === AgentConnectionType.Direct &&
          agent.status === CustomAgentStatus.Active
        ) {
          stopPolling();
          finishConnected('Agent connected', 'Your agent connected directly and is ready to use');
        }
      } catch (error) {
        console.error('Error polling connection status:', error);
      }
    };

    // Poll immediately, then every 3 seconds
    pollStatus();
    pollIntervalRef.current = setInterval(pollStatus, 3000);
  }, [getConnectorStatus, getCustomAgent, updateAgent, toast, onSuccess, onClose]);

  // Format expiration time
  const formatExpiration = (date: Date) => {
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    if (diffMs <= 0) return 'Expired';

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes}m remaining`;
    }
    return `${diffMinutes}m remaining`;
  };

  // Detect user's OS
  const detectedOS = useMemo(() => {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('mac')) return 'macOS' as const;
    if (ua.includes('win')) return 'Windows' as const;
    return 'Linux' as const;
  }, []);

  // CLI commands
  const downloadCommand = detectedOS === 'macOS'
    ? 'pip3 install --user --upgrade agentwatch-cli'
    : detectedOS === 'Windows'
    ? 'pip install --user --upgrade agentwatch-cli'
    : 'pip install --user --upgrade agentwatch-cli';
  const enrollCommand = `python3 -m agentwatch_cli enroll --code ${enrollmentCode}`;
  const installServiceCommand = detectedOS === 'macOS'
    ? 'python3 -m agentwatch_cli install-service'
    : detectedOS === 'Windows'
    ? 'python -m agentwatch_cli install-service'
    : 'sudo python3 -m agentwatch_cli install-service';
  const masterCommand = `${downloadCommand} && ${enrollCommand} && ${installServiceCommand}`;

  // Copy command to clipboard
  const copyCommand = (command: string) => {
    navigator.clipboard.writeText(command);
    toast({
      title: 'Copied to clipboard',
      status: 'success',
      duration: 2000,
    });
  };

  // Regenerate enrollment code
  const handleRegenerateEnrollment = async () => {
    if (!createdAgentId) return;

    try {
      const enrollmentResult = await generateEnrollment({
        variables: { agentId: createdAgentId },
      });

      const enrollment = enrollmentResult.data?.generateConnectorEnrollment;
      if (!enrollment) {
        throw new Error('Failed to regenerate enrollment code');
      }

      setEnrollmentCode(enrollment.enrollmentCode);
      setExpiresAt(new Date(enrollment.expiresAt));
      setConnectorStatus(null);
      setEnrollmentStatus(null);

      // Restart polling
      startPolling(createdAgentId);

      toast({
        title: 'New enrollment code generated',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to regenerate code',
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Automatic tab: create a connector agent + enrollment code, then surface a
  // copyable prompt the user hands to their agent so it self-registers.
  const handleGenerateAutomatic = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Name is required', status: 'error', duration: 3000 });
      return;
    }

    try {
      const result = await createAgent({
        variables: {
          input: {
            name: formData.name,
            description: formData.description || 'AgentWatch monitored agent',
            apiEndpoint: '',
            apiAuthType: 'MOLTBOT',
            systemPrompt: 'AgentWatch monitored agent',
            temperature: formData.temperature,
            maxTokens: formData.maxTokens,
          },
        },
      });

      const agentId = result.data?.createCustomAgent?.id;
      if (!agentId) {
        throw new Error('Failed to create agent');
      }
      setCreatedAgentId(agentId);

      const enrollmentResult = await generateEnrollment({
        variables: { agentId },
      });
      const enrollment = enrollmentResult.data?.generateConnectorEnrollment;
      if (!enrollment) {
        throw new Error('Failed to generate enrollment code');
      }

      setEnrollmentCode(enrollment.enrollmentCode);
      setExpiresAt(new Date(enrollment.expiresAt));
      setAutomaticGenerated(true);
      startPolling(agentId);
      onSuccess();
    } catch (error: any) {
      const graphQLError = error?.graphQLErrors?.[0];
      const errorMessage =
        graphQLError?.extensions?.userMessage ||
        graphQLError?.message ||
        error?.message ||
        'Unknown error';
      const isQuotaError = graphQLError?.extensions?.code === 'QUOTA_EXCEEDED';
      toast({
        title: isQuotaError ? 'Usage Limit Reached' : 'Error creating agent',
        description: errorMessage,
        status: 'error',
        duration: isQuotaError ? 8000 : 5000,
      });
    }
  };

  const [formData, setFormData] = useState({
    connectionType: 'MOLTBOT' as ConnectionType,
    name: '',
    description: '',
    avatarUrl: '',
    // API specific (Custom API only)
    apiEndpoint: '',
    apiAuthType: 'API_KEY',
    apiKey: '',
    // Advanced
    systemPrompt: '',
    temperature: 0.7,
    maxTokens: 4000,
  });

  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const isMoltbot = formData.connectionType === 'MOLTBOT';

  // Connection test state
  const [connectionTest, setConnectionTest] = useState<{
    status: 'idle' | 'testing' | 'success' | 'error';
    message?: string;
    responseTime?: number;
  }>({ status: 'idle' });

  // Test connection function (Custom API only)
  const testConnection = useCallback(async () => {
    if (isMoltbot) return; // Moltbot uses connector enrollment, not direct connection test

    const endpoint = formData.apiEndpoint;

    if (!endpoint.trim()) {
      toast({
        title: 'API Endpoint is required',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setConnectionTest({ status: 'testing' });
    const startTime = Date.now();

    // Test HTTP endpoint for Custom API
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(endpoint, {
        method: 'HEAD',
        signal: controller.signal,
        mode: 'no-cors', // Allow testing even without CORS headers
      });

      clearTimeout(timeout);
      const responseTime = Date.now() - startTime;

      // With no-cors mode, we can't read the response, but if we get here, the endpoint exists
      setConnectionTest({
        status: 'success',
        message: 'Endpoint is reachable',
        responseTime,
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        setConnectionTest({
          status: 'error',
          message: 'Connection timed out (10s). Check if the endpoint is accessible.',
        });
      } else {
        setConnectionTest({
          status: 'error',
          message: 'Failed to reach endpoint. Check the URL and try again.',
        });
      }
    }
  }, [isMoltbot, formData.apiEndpoint, toast]);

  // Reset connection test when endpoint changes (Custom API only)
  const handleEndpointChange = (value: string) => {
    setFormData({ ...formData, apiEndpoint: value });
    // Reset test status when endpoint changes
    if (connectionTest.status !== 'idle') {
      setConnectionTest({ status: 'idle' });
    }
  };

  // Colors based on connection type
  // OpenClaw theme: Coral Red (#ff4d4d, #e63946) and Cyan (#00e5cc, #14b8a6)
  const accentColor = isMoltbot ? '#e63946' : '#00477D';
  const accentColorLight = isMoltbot ? 'rgba(230,57,70,0.1)' : 'rgba(0,71,125,0.1)';
  const gradientStart = isMoltbot ? '#ff4d4d' : '#00477D';
  const gradientEnd = isMoltbot ? '#00e5cc' : '#486884';

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast({
        title: 'Name is required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (!isMoltbot && !formData.apiEndpoint.trim()) {
      toast({
        title: 'API Endpoint is required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      let apiCredentials: string | undefined;
      if (!isMoltbot && formData.apiKey) {
        if (formData.apiAuthType === 'API_KEY') {
          apiCredentials = JSON.stringify({ apiKey: formData.apiKey });
        } else if (formData.apiAuthType === 'BEARER') {
          apiCredentials = JSON.stringify({ token: formData.apiKey });
        } else if (formData.apiAuthType === 'OAUTH') {
          apiCredentials = JSON.stringify({ accessToken: formData.apiKey });
        }
      }

      const result = await createAgent({
        variables: {
          input: {
            name: formData.name,
            description: formData.description || (isMoltbot ? 'Moltbot (OpenClaw) Agent' : undefined),
            avatarUrl: formData.avatarUrl || undefined,
            apiEndpoint: formData.apiEndpoint,
            apiAuthType: isMoltbot ? 'MOLTBOT' : formData.apiAuthType,
            apiCredentials: apiCredentials,
            systemPrompt: formData.systemPrompt || (isMoltbot ? 'Moltbot/OpenClaw personal AI assistant' : undefined),
            temperature: formData.temperature,
            maxTokens: formData.maxTokens,
          },
        },
      });

      const agentId = result.data?.createCustomAgent?.id;

      if (isMoltbot && agentId) {
        // For Moltbot: generate enrollment code and show enrollment step
        setCreatedAgentId(agentId);

        try {
          const enrollmentResult = await generateEnrollment({
            variables: { agentId },
          });

          const enrollment = enrollmentResult.data?.generateConnectorEnrollment;
          if (enrollment) {
            setEnrollmentCode(enrollment.enrollmentCode);
            setExpiresAt(new Date(enrollment.expiresAt));
            setStep('enrollment');
            startPolling(agentId);

            toast({
              title: 'Moltbot created',
              description: 'Complete the enrollment to connect your Moltbot',
              status: 'success',
              duration: 3000,
            });

            // Call onSuccess to refresh the agent list
            onSuccess();
          }
        } catch (enrollError) {
          toast({
            title: 'Agent created but enrollment failed',
            description: 'You can generate the enrollment code later from the agent settings',
            status: 'warning',
            duration: 5000,
          });
          onSuccess();
          handleClose();
        }
      } else {
        // For Custom API: set status based on connection test and close
        if (agentId && connectionTest.status === 'success') {
          await updateAgent({
            variables: {
              id: agentId,
              input: { status: CustomAgentStatus.Active },
            },
          });
        }

        toast({
          title: 'Agent added successfully',
          description: connectionTest.status === 'success'
            ? 'Your agent is connected and active'
            : 'Your agent was added but connection was not verified',
          status: 'success',
          duration: 3000,
        });

        onSuccess();
        handleClose();
      }
    } catch (error: any) {
      // Extract user-friendly error message from GraphQL error
      const graphQLError = error?.graphQLErrors?.[0];
      const errorMessage = graphQLError?.extensions?.userMessage
        || graphQLError?.message
        || error?.message
        || 'Unknown error';

      // Check if it's a quota exceeded error
      const isQuotaError = graphQLError?.extensions?.code === 'QUOTA_EXCEEDED';

      toast({
        title: isQuotaError ? 'Usage Limit Reached' : 'Error adding agent',
        description: errorMessage,
        status: 'error',
        duration: isQuotaError ? 8000 : 5000,
      });
    }
  };

  const resetForm = () => {
    setFormData({
      connectionType: 'MOLTBOT',
      name: '',
      description: '',
      avatarUrl: '',
      apiEndpoint: '',
      apiAuthType: 'API_KEY',
      apiKey: '',
      systemPrompt: '',
      temperature: 0.7,
      maxTokens: 4000,
    });
    setShowAdvanced(false);
    setConnectionTest({ status: 'idle' });
    // Reset enrollment state
    setStep('form');
    setMode('automatic');
    setAutomaticGenerated(false);
    setCreatedAgentId(null);
    setEnrollmentCode(null);
    setExpiresAt(null);
    setConnectorStatus(null);
    setEnrollmentStatus(null);
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const canSubmit = isMoltbot && canCreateAgent && formData.name.trim();

  const isExpired = expiresAt ? expiresAt.getTime() < Date.now() : false;
  const isConnectorOnline = connectorStatus === 'ONLINE';

  // We're showing a "result" (prompt/enrollment + live status) rather than a form
  // when the Automatic tab has generated, or the Manual Moltbot flow reached enrollment.
  const showResult =
    (mode === 'automatic' && automaticGenerated) ||
    (mode === 'manual' && step === 'enrollment');

  // The copyable prompt the user hands to their agent (Automatic tab). The agent
  // is already pre-created in the operator's workspace; the one-time enrollment
  // code authenticates it into THAT record. skill.md covers both paths — a public
  // URL claims directly (/api/agent/claim), otherwise it enrolls the connector.
  const setupPrompt = `You've been pre-registered on AgentWatch. Connect yourself by following the instructions at agentwatch.helivan.io/skill.md using your enrollment code: ${enrollmentCode} (expires in 24h).`;

  // Live connection-status panel — reused by both Automatic and Manual flows.
  const connectionStatusPanel = (
    <Box
      bg={isConnectorOnline ? 'rgba(34,197,94,0.1)' : 'rgba(100,116,139,0.1)'}
      borderRadius="12px"
      p={4}
      border="1px solid"
      borderColor={isConnectorOnline ? 'rgba(34,197,94,0.3)' : 'rgba(100,116,139,0.2)'}
    >
      <HStack spacing={3}>
        {isConnectorOnline ? (
          <IoCheckmarkCircle size={24} color="#22c55e" />
        ) : enrollmentStatus === ConnectorEnrollmentStatus.Enrolled ? (
          <Spinner size="sm" color="#64748b" />
        ) : (
          <Box
            w="24px"
            h="24px"
            borderRadius="full"
            border="2px solid"
            borderColor="#cbd5e1"
            animation={enrollmentStatus === ConnectorEnrollmentStatus.Pending ? `${pulse} 2s ease-in-out infinite` : undefined}
          />
        )}
        <Box flex={1}>
          <Text fontSize="14px" fontWeight={600} color={isConnectorOnline ? '#22c55e' : '#1a1a2e'}>
            {isConnectorOnline
              ? 'Connector Online'
              : enrollmentStatus === ConnectorEnrollmentStatus.Enrolled
              ? 'Waiting for connector to start...'
              : 'Waiting for enrollment...'}
          </Text>
          <Text fontSize="12px" color="#64748b">
            {isConnectorOnline
              ? 'Your agent is ready to use'
              : enrollmentStatus === ConnectorEnrollmentStatus.Enrolled
              ? 'Almost there — the connector is starting up'
              : 'Run the setup on your agent to finish connecting'}
          </Text>
        </Box>
        {!isConnectorOnline && (
          <Tooltip label="Refresh status">
            <IconButton
              aria-label="Refresh"
              icon={<RiRefreshLine />}
              size="sm"
              variant="ghost"
              onClick={() => createdAgentId && startPolling(createdAgentId)}
            />
          </Tooltip>
        )}
      </HStack>
    </Box>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" scrollBehavior="inside" isCentered motionPreset="slideInBottom">
      <ModalOverlay
        bg="blackAlpha.600"
        backdropFilter="blur(10px)"
      />
      <ModalContent
        bg="transparent"
        boxShadow="none"
        mx={{ base: 2, sm: 4 }}
        overflow="visible"
        maxH="90vh"
      >
        {/* Outer glow effect */}
        <Box
          position="absolute"
          top="-2px"
          left="-2px"
          right="-2px"
          bottom="-2px"
          borderRadius={{ base: '18px', md: '28px' }}
          bg={`linear-gradient(135deg, ${gradientStart}40 0%, ${gradientEnd}30 50%, ${gradientStart}40 100%)`}
          backgroundSize="200% 200%"
          animation={`${gradientShift} 5s ease infinite`}
          filter="blur(8px)"
          opacity={0.6}
          zIndex={-1}
        />

        {/* Main modal container */}
        <Box
          bg="white"
          borderRadius={{ base: '16px', md: '24px' }}
          overflow="hidden"
          boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          display="flex"
          flexDirection="column"
          maxH="90vh"
        >
          {/* Header with gradient accent */}
          <Box position="relative" overflow="hidden" flexShrink={0}>
            {/* Gradient bar */}
            <Box
              h="4px"
              bgGradient={`linear(to-r, ${gradientStart}, ${gradientEnd}, ${gradientStart})`}
              backgroundSize="200% 100%"
              animation={`${gradientShift} 3s ease infinite`}
            />

            <Flex
              px={6}
              pt={6}
              pb={4}
              align="center"
              gap={4}
            >
              {/* Icon with gradient background */}
              <Flex
                w="56px"
                h="56px"
                borderRadius="16px"
                bgGradient={`linear(135deg, ${gradientStart} 0%, ${gradientEnd} 100%)`}
                align="center"
                justify="center"
                boxShadow={`0 4px 14px ${accentColor}50`}
              >
                <MdOutlineSmartToy size={28} color="white" />
              </Flex>

              <Box flex={1}>
                <Text
                  fontSize="20px"
                  fontWeight={700}
                  color="#1a1a2e"
                  letterSpacing="-0.02em"
                >
                  {showResult ? 'Connect Your Agent' : 'Add Agent'}
                </Text>
                <Text
                  fontSize="14px"
                  color="#64748b"
                  mt={0.5}
                >
                  {showResult
                    ? 'Hand the prompt to your agent, or run the setup yourself'
                    : mode === 'automatic'
                    ? 'Get a prompt to hand to your agent'
                    : isMoltbot
                    ? 'Connect your OpenClaw personal AI assistant'
                    : 'Configure your custom AI agent'}
                </Text>
              </Box>

              <ModalCloseButton
                position="relative"
                top={0}
                right={0}
                w="36px"
                h="36px"
                borderRadius="12px"
                bg="#f1f5f9"
                color="#64748b"
                _hover={{ bg: '#e2e8f0', color: '#1a1a2e' }}
                transition="all 0.2s"
              />
            </Flex>
          </Box>

          {/* Body */}
          <ModalBody px={6} py={5} overflowY="auto" flex={1}>
            <Tabs
              index={mode === 'automatic' ? 0 : 1}
              onChange={(i) => {
                if (!showResult) setMode(i === 0 ? 'automatic' : 'manual');
              }}
              variant="unstyled"
            >
              {!showResult && (
                <TabList mb={5} bg="#f1f5f9" borderRadius="12px" p={1} gap={1}>
                  <Tab
                    flex={1}
                    fontSize="14px"
                    fontWeight={600}
                    borderRadius="10px"
                    color="#64748b"
                    _selected={{ bg: 'white', color: '#1a1a2e', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
                  >
                    Automatic
                  </Tab>
                  <Tab
                    flex={1}
                    fontSize="14px"
                    fontWeight={600}
                    borderRadius="10px"
                    color="#64748b"
                    _selected={{ bg: 'white', color: '#1a1a2e', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
                  >
                    Manual
                  </Tab>
                </TabList>
              )}
              <TabPanels>
                {/* Automatic: copyable prompt for the user to hand to their agent */}
                <TabPanel px={0} py={0}>
                  <Stack spacing={5}>
                    {!automaticGenerated ? (
                      <>
                        <Flex
                          bg="rgba(230,57,70,0.06)"
                          borderRadius="16px"
                          p={4}
                          gap={4}
                          align="flex-start"
                          border="1px solid"
                          borderColor="rgba(230,57,70,0.1)"
                        >
                          <Flex
                            w="40px"
                            h="40px"
                            borderRadius="12px"
                            bg="rgba(230,57,70,0.15)"
                            align="center"
                            justify="center"
                            flexShrink={0}
                          >
                            <MdOutlineSmartToy size={20} color="#e63946" />
                          </Flex>
                          <Box>
                            <Text fontSize="13px" color="#475569" lineHeight="1.6">
                              Name your agent, then we'll generate a setup prompt you can paste
                              directly to your agent so it connects itself. Works for both
                              connector-based and API/DIRECT agents.
                            </Text>
                          </Box>
                        </Flex>

                        {!canCreateAgent && (
                          <Flex
                            bg="rgba(220,38,38,0.06)"
                            borderRadius="12px"
                            p={3}
                            gap={3}
                            align="center"
                            border="1px solid"
                            borderColor="rgba(220,38,38,0.2)"
                          >
                            <RiErrorWarningLine size={18} color="#dc2626" />
                            <Text fontSize="13px" color="#475569">
                              You've used {currentAgentCount} of {maxAgents} agents.{' '}
                              <Text
                                as="span"
                                color="#00477D"
                                fontWeight={600}
                                cursor="pointer"
                                _hover={{ textDecoration: 'underline' }}
                                onClick={() => {
                                  onClose();
                                  navigate('/pricing');
                                }}
                              >
                                Upgrade
                              </Text>{' '}
                              to add more.
                            </Text>
                          </Flex>
                        )}

                        <Box bg="#f8fafc" borderRadius="16px" p={5} border="1px solid" borderColor="#e2e8f0">
                          <Stack spacing={4}>
                            <FormControl isRequired>
                              <FormLabel fontSize="13px" fontWeight={600} color="#475569" mb={2} textTransform="uppercase" letterSpacing="0.05em">
                                Name
                              </FormLabel>
                              <Input
                                placeholder="My Agent"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                size="lg"
                                bg="white"
                                border="2px solid"
                                borderColor="#e2e8f0"
                                borderRadius="14px"
                                fontSize="15px"
                                color="#1a1a2e"
                                _placeholder={{ color: '#94a3b8' }}
                                _hover={{ borderColor: '#cbd5e1' }}
                                _focus={{ borderColor: '#e63946', boxShadow: '0 0 0 3px rgba(230,57,70,0.1)' }}
                                transition="all 0.2s"
                              />
                            </FormControl>
                            <FormControl>
                              <FormLabel fontSize="13px" fontWeight={600} color="#475569" mb={2} textTransform="uppercase" letterSpacing="0.05em">
                                Description
                              </FormLabel>
                              <Textarea
                                placeholder="Describe your agent..."
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                rows={2}
                                bg="white"
                                border="2px solid"
                                borderColor="#e2e8f0"
                                borderRadius="14px"
                                fontSize="15px"
                                color="#1a1a2e"
                                _placeholder={{ color: '#94a3b8' }}
                                _hover={{ borderColor: '#cbd5e1' }}
                                _focus={{ borderColor: '#e63946', boxShadow: '0 0 0 3px rgba(230,57,70,0.1)' }}
                                transition="all 0.2s"
                                resize="none"
                              />
                            </FormControl>
                          </Stack>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Box>
                          <Text fontSize="14px" fontWeight={600} color="#1a1a2e" mb={2}>
                            Copy this prompt and paste it to your agent:
                          </Text>
                          <Flex bg="#1a1a2e" borderRadius="12px" p={3} align="flex-start" justify="space-between" gap={2}>
                            <Code
                              bg="transparent"
                              color="#00e5cc"
                              fontSize="12px"
                              fontFamily="mono"
                              whiteSpace="pre-wrap"
                              wordBreak="break-word"
                              flex={1}
                            >
                              {setupPrompt}
                            </Code>
                            <Tooltip label="Copy prompt">
                              <IconButton
                                aria-label="Copy prompt"
                                icon={<IoCopyOutline />}
                                size="sm"
                                variant="ghost"
                                color="white"
                                _hover={{ bg: 'whiteAlpha.200' }}
                                onClick={() => copyCommand(setupPrompt)}
                                flexShrink={0}
                              />
                            </Tooltip>
                          </Flex>
                          <HStack mt={2} spacing={2}>
                            <IoTimeOutline size={14} color={isExpired ? '#ef4444' : '#64748b'} />
                            <Text fontSize="12px" color={isExpired ? '#ef4444' : '#64748b'}>
                              {expiresAt && formatExpiration(expiresAt)}
                            </Text>
                            {isExpired && (
                              <Box
                                as="button"
                                fontSize="12px"
                                color="#e63946"
                                fontWeight={600}
                                onClick={handleRegenerateEnrollment}
                                _hover={{ textDecoration: 'underline' }}
                              >
                                Generate new code
                              </Box>
                            )}
                          </HStack>
                        </Box>

                        {connectionStatusPanel}
                      </>
                    )}
                  </Stack>
                </TabPanel>

                {/* Manual: the original fill-in-the-details form */}
                <TabPanel px={0} py={0}>
            <Stack spacing={5}>
              {/* Connection Type Selector - Only show in form step */}
              {step === 'form' && (
              <HStack spacing={3}>
                <Box
                  flex={1}
                  px={4}
                  py={3}
                  borderRadius="14px"
                  border="2px solid"
                  borderColor={isMoltbot ? '#e63946' : '#e2e8f0'}
                  bg={isMoltbot ? 'rgba(230,57,70,0.05)' : 'white'}
                  cursor="pointer"
                  transition="all 0.2s"
                  onClick={() => setFormData({ ...formData, connectionType: 'MOLTBOT' })}
                  _hover={{ borderColor: isMoltbot ? '#e63946' : '#cbd5e1' }}
                >
                  <HStack spacing={3}>
                    <Flex
                      w="36px"
                      h="36px"
                      borderRadius="10px"
                      bg={isMoltbot ? 'rgba(230,57,70,0.15)' : '#f1f5f9'}
                      align="center"
                      justify="center"
                    >
                      <MdOutlineSmartToy size={18} color={isMoltbot ? '#e63946' : '#64748b'} />
                    </Flex>
                    <Box>
                      <Text fontSize="14px" fontWeight={600} color={isMoltbot ? '#e63946' : '#475569'}>
                        Moltbot / OpenClaw
                      </Text>
                      <Text fontSize="12px" color="#94a3b8">
                        Self-hosted AI assistant
                      </Text>
                    </Box>
                  </HStack>
                </Box>

                <Box
                  flex={1}
                  px={4}
                  py={3}
                  borderRadius="14px"
                  border="2px solid"
                  borderColor={!isMoltbot ? '#00477D' : '#e2e8f0'}
                  bg={!isMoltbot ? 'rgba(0,71,125,0.05)' : 'white'}
                  cursor="pointer"
                  transition="all 0.2s"
                  onClick={() => setFormData({ ...formData, connectionType: 'API' })}
                  _hover={{ borderColor: !isMoltbot ? '#00477D' : '#cbd5e1' }}
                >
                  <HStack spacing={3}>
                    <Flex
                      w="36px"
                      h="36px"
                      borderRadius="10px"
                      bg={!isMoltbot ? 'rgba(0,71,125,0.15)' : '#f1f5f9'}
                      align="center"
                      justify="center"
                    >
                      <RiKeyLine size={18} color={!isMoltbot ? '#00477D' : '#64748b'} />
                    </Flex>
                    <Box>
                      <Text fontSize="14px" fontWeight={600} color={!isMoltbot ? '#00477D' : '#475569'}>
                        Custom API
                      </Text>
                      <Text fontSize="12px" color="#94a3b8">
                        REST API endpoint
                      </Text>
                    </Box>
                  </HStack>
                </Box>
              </HStack>
              )}

              {/* Limit Warning - Only show in form step */}
              {step === 'form' && !canCreateAgent && (
                <Flex
                  bg="rgba(220,38,38,0.06)"
                  borderRadius="16px"
                  p={4}
                  gap={4}
                  align="center"
                  border="1px solid"
                  borderColor="rgba(220,38,38,0.2)"
                >
                  <Flex
                    w="40px"
                    h="40px"
                    borderRadius="12px"
                    bg="rgba(220,38,38,0.15)"
                    align="center"
                    justify="center"
                    flexShrink={0}
                  >
                    <RiErrorWarningLine size={20} color="#dc2626" />
                  </Flex>
                  <Box flex={1}>
                    <Text fontSize="14px" fontWeight={600} color="#dc2626">
                      Agent Limit Reached
                    </Text>
                    <Text fontSize="13px" color="#475569" lineHeight="1.6">
                      You've used {currentAgentCount} of {maxAgents} agents.{' '}
                      <Text
                        as="span"
                        color="#00477D"
                        fontWeight={600}
                        cursor="pointer"
                        _hover={{ textDecoration: 'underline' }}
                        onClick={() => {
                          onClose();
                          navigate('/pricing');
                        }}
                      >
                        Upgrade your plan
                      </Text>
                      {' '}to add more agents.
                    </Text>
                  </Box>
                </Flex>
              )}

              {/* Moltbot Info Card */}
              {isMoltbot && step === 'form' && (
                <Flex
                  bg="rgba(230,57,70,0.06)"
                  borderRadius="16px"
                  p={4}
                  gap={4}
                  align="flex-start"
                  border="1px solid"
                  borderColor="rgba(230,57,70,0.1)"
                >
                  <Flex
                    w="40px"
                    h="40px"
                    borderRadius="12px"
                    bg="rgba(230,57,70,0.15)"
                    align="center"
                    justify="center"
                    flexShrink={0}
                  >
                    <SiGithub size={20} color="#e63946" />
                  </Flex>
                  <Box>
                    <Text
                      fontSize="13px"
                      color="#475569"
                      lineHeight="1.6"
                    >
                      Moltbot is powered by{' '}
                      <Link
                        href="https://github.com/openclaw/openclaw"
                        isExternal
                        color="#e63946"
                        fontWeight={600}
                      >
                        OpenClaw
                      </Link>
                      , a self-hosted AI assistant. After creating your agent, you'll get a secure enrollment code to connect your local gateway.
                    </Text>
                  </Box>
                </Flex>
              )}

              {/* Basic Information Section - Form Step (Moltbot only) */}
              {isMoltbot && step === 'form' && (
              <Box
                bg="#f8fafc"
                borderRadius="16px"
                p={5}
                border="1px solid"
                borderColor="#e2e8f0"
              >
                <Flex align="center" gap={3} mb={4}>
                  <Flex
                    w="32px"
                    h="32px"
                    borderRadius="10px"
                    bg={accentColorLight}
                    align="center"
                    justify="center"
                  >
                    <MdOutlineSmartToy size={16} color={accentColor} />
                  </Flex>
                  <Text fontSize="15px" fontWeight={600} color="#1a1a2e">
                    Basic Information
                  </Text>
                </Flex>

                <Stack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel
                      fontSize="13px"
                      fontWeight={600}
                      color="#475569"
                      mb={2}
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      Name
                    </FormLabel>
                    <Input
                      placeholder={isMoltbot ? "My Moltbot" : "My Custom Agent"}
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      size="lg"
                      bg="white"
                      border="2px solid"
                      borderColor="#e2e8f0"
                      borderRadius="14px"
                      fontSize="15px"
                      color="#1a1a2e"
                      _placeholder={{ color: '#94a3b8' }}
                      _hover={{ borderColor: '#cbd5e1' }}
                      _focus={{
                        borderColor: accentColor,
                        boxShadow: `0 0 0 3px ${accentColorLight}`,
                      }}
                      transition="all 0.2s"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel
                      fontSize="13px"
                      fontWeight={600}
                      color="#475569"
                      mb={2}
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      Description
                    </FormLabel>
                    <Textarea
                      placeholder={isMoltbot ? "Describe your Moltbot..." : "Describe what this agent does..."}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                      bg="white"
                      border="2px solid"
                      borderColor="#e2e8f0"
                      borderRadius="14px"
                      fontSize="15px"
                      color="#1a1a2e"
                      _placeholder={{ color: '#94a3b8' }}
                      _hover={{ borderColor: '#cbd5e1' }}
                      _focus={{
                        borderColor: accentColor,
                        boxShadow: `0 0 0 3px ${accentColorLight}`,
                      }}
                      transition="all 0.2s"
                      resize="none"
                    />
                  </FormControl>

                  {!isMoltbot && (
                    <HStack spacing={4}>
                      <FormControl flex={1}>
                        <FormLabel
                          fontSize="13px"
                          fontWeight={600}
                          color="#475569"
                          mb={2}
                          textTransform="uppercase"
                          letterSpacing="0.05em"
                        >
                          Avatar URL
                        </FormLabel>
                        <Input
                          placeholder="https://..."
                          value={formData.avatarUrl}
                          onChange={(e) => setFormData({ ...formData, avatarUrl: e.target.value })}
                          bg="white"
                          border="2px solid"
                          borderColor="#e2e8f0"
                          borderRadius="14px"
                          fontSize="15px"
                          color="#1a1a2e"
                          _placeholder={{ color: '#94a3b8' }}
                          _hover={{ borderColor: '#cbd5e1' }}
                          _focus={{
                            borderColor: accentColor,
                            boxShadow: `0 0 0 3px ${accentColorLight}`,
                          }}
                          transition="all 0.2s"
                        />
                      </FormControl>
                    </HStack>
                  )}
                </Stack>
              </Box>
              )}

              {/* Moltbot Enrollment Step */}
              {isMoltbot && step === 'enrollment' && (
                <Box
                  bg="#f8fafc"
                  borderRadius="16px"
                  p={5}
                  border="1px solid"
                  borderColor="#e2e8f0"
                >
                  <Stack spacing={4}>
                    {/* OS-Detected Summary Banner */}
                    <Flex
                      bg="rgba(230,57,70,0.06)"
                      borderRadius="12px"
                      p={4}
                      gap={3}
                      align="center"
                      border="1px solid"
                      borderColor="rgba(230,57,70,0.15)"
                    >
                      <Flex
                        w="36px"
                        h="36px"
                        borderRadius="10px"
                        bg="rgba(230,57,70,0.15)"
                        align="center"
                        justify="center"
                        flexShrink={0}
                      >
                        <MdOutlineSmartToy size={18} color="#e63946" />
                      </Flex>
                      <Box flex={1}>
                        <Text fontSize="14px" fontWeight={600} color="#1a1a2e">
                          Setup for {detectedOS}
                        </Text>
                        <Text fontSize="12px" color="#64748b">
                          Download, enroll, and install your Moltbot in 3 steps
                        </Text>
                      </Box>
                      <Badge
                        colorScheme={detectedOS === 'macOS' ? 'purple' : detectedOS === 'Windows' ? 'blue' : 'green'}
                        borderRadius="full"
                        px={2}
                        fontSize="11px"
                      >
                        {detectedOS}
                      </Badge>
                    </Flex>

                    {/* Gateway URL */}
                    <FormControl>
                      <FormLabel
                        fontSize="13px"
                        fontWeight={600}
                        color="#475569"
                        mb={2}
                        textTransform="uppercase"
                        letterSpacing="0.05em"
                      >
                        Gateway URL (optional)
                      </FormLabel>
                      <Input
                        placeholder="ws://127.0.0.1:18789"
                        value={formData.apiEndpoint}
                        onChange={(e) => setFormData({ ...formData, apiEndpoint: e.target.value })}
                        size="lg"
                        bg="white"
                        border="2px solid"
                        borderColor="#e2e8f0"
                        borderRadius="14px"
                        fontSize="15px"
                        color="#1a1a2e"
                        _placeholder={{ color: '#94a3b8' }}
                        _hover={{ borderColor: '#cbd5e1' }}
                        _focus={{
                          borderColor: '#e63946',
                          boxShadow: '0 0 0 3px rgba(230,57,70,0.1)',
                        }}
                        transition="all 0.2s"
                      />
                      <Text fontSize="11px" color="#94a3b8" mt={1}>
                        WebSocket address of your Moltbot gateway (e.g. ws://127.0.0.1:18789)
                      </Text>
                    </FormControl>

                    {/* Master Command */}
                    <Box>
                      <Text fontSize="14px" fontWeight={600} color="#1a1a2e" mb={2}>
                        Run this on your machine:
                      </Text>
                      <Flex
                        bg="#1a1a2e"
                        borderRadius="12px"
                        p={3}
                        align="flex-start"
                        justify="space-between"
                        gap={2}
                      >
                        <Code
                          bg="transparent"
                          color="#00e5cc"
                          fontSize="12px"
                          fontFamily="mono"
                          whiteSpace="pre-wrap"
                          wordBreak="break-all"
                          flex={1}
                        >
                          {masterCommand}
                        </Code>
                        <Tooltip label="Copy command">
                          <IconButton
                            aria-label="Copy"
                            icon={<IoCopyOutline />}
                            size="sm"
                            variant="ghost"
                            color="white"
                            _hover={{ bg: 'whiteAlpha.200' }}
                            onClick={() => copyCommand(masterCommand)}
                            flexShrink={0}
                          />
                        </Tooltip>
                      </Flex>
                      {/* Expiration info */}
                      <HStack mt={2} spacing={2}>
                        <IoTimeOutline size={14} color={isExpired ? '#ef4444' : '#64748b'} />
                        <Text fontSize="12px" color={isExpired ? '#ef4444' : '#64748b'}>
                          {expiresAt && formatExpiration(expiresAt)}
                        </Text>
                        {isExpired && (
                          <Box
                            as="button"
                            fontSize="12px"
                            color="#e63946"
                            fontWeight={600}
                            onClick={handleRegenerateEnrollment}
                            _hover={{ textDecoration: 'underline' }}
                          >
                            Generate new code
                          </Box>
                        )}
                      </HStack>
                    </Box>

                    {/* Breakdown */}
                    <Box>
                      <Text fontSize="13px" fontWeight={600} color="#64748b" mb={3} textTransform="uppercase" letterSpacing="0.05em">
                        Breakdown
                      </Text>
                      <Stack spacing={3}>
                        {/* Download */}
                        <Flex gap={3} align="flex-start">
                          <Badge colorScheme="red" borderRadius="full" px={2} mt={0.5} flexShrink={0}>1</Badge>
                          <Box flex={1}>
                            <Text fontSize="13px" fontWeight={600} color="#1a1a2e">
                              Download
                            </Text>
                            <Text fontSize="12px" color="#64748b" mb={2}>
                              Installs the Agentwatch CLI
                            </Text>
                            <Flex
                              bg="#1a1a2e"
                              borderRadius="10px"
                              px={3}
                              py={2}
                              align="center"
                              justify="space-between"
                            >
                              <Code bg="transparent" color="#00e5cc" fontSize="12px" fontFamily="mono" whiteSpace="pre-wrap" wordBreak="break-all">
                                {downloadCommand}
                              </Code>
                              <Tooltip label="Copy">
                                <IconButton aria-label="Copy" icon={<IoCopyOutline />} size="xs" variant="ghost" color="white" _hover={{ bg: 'whiteAlpha.200' }} onClick={() => copyCommand(downloadCommand)} />
                              </Tooltip>
                            </Flex>
                          </Box>
                        </Flex>

                        {/* Enroll */}
                        <Flex gap={3} align="flex-start">
                          <Badge colorScheme="red" borderRadius="full" px={2} mt={0.5} flexShrink={0}>2</Badge>
                          <Box flex={1}>
                            <Text fontSize="13px" fontWeight={600} color="#1a1a2e">
                              Enroll
                            </Text>
                            <Text fontSize="12px" color="#64748b" mb={2}>
                              Registers your agent using a secure enrollment code
                            </Text>
                            <Flex
                              bg="#1a1a2e"
                              borderRadius="10px"
                              px={3}
                              py={2}
                              align="center"
                              justify="space-between"
                            >
                              <Code bg="transparent" color="#00e5cc" fontSize="12px" fontFamily="mono" whiteSpace="pre-wrap" wordBreak="break-all">
                                {enrollCommand}
                              </Code>
                              <Tooltip label="Copy">
                                <IconButton aria-label="Copy" icon={<IoCopyOutline />} size="xs" variant="ghost" color="white" _hover={{ bg: 'whiteAlpha.200' }} onClick={() => copyCommand(enrollCommand)} />
                              </Tooltip>
                            </Flex>
                          </Box>
                        </Flex>

                        {/* Install */}
                        <Flex gap={3} align="flex-start">
                          <Badge colorScheme="red" borderRadius="full" px={2} mt={0.5} flexShrink={0}>3</Badge>
                          <Box flex={1}>
                            <Text fontSize="13px" fontWeight={600} color="#1a1a2e">
                              Install
                            </Text>
                            <Text fontSize="12px" color="#64748b" mb={2}>
                              Runs as a background service that auto-starts on boot
                            </Text>
                            <Flex
                              bg="#1a1a2e"
                              borderRadius="10px"
                              px={3}
                              py={2}
                              align="center"
                              justify="space-between"
                            >
                              <Code bg="transparent" color="#00e5cc" fontSize="12px" fontFamily="mono">
                                {installServiceCommand}
                              </Code>
                              <Tooltip label="Copy">
                                <IconButton aria-label="Copy" icon={<IoCopyOutline />} size="xs" variant="ghost" color="white" _hover={{ bg: 'whiteAlpha.200' }} onClick={() => copyCommand(installServiceCommand)} />
                              </Tooltip>
                            </Flex>
                          </Box>
                        </Flex>
                      </Stack>
                    </Box>

                    {/* Connection Status */}
                    {connectionStatusPanel}
                  </Stack>
                </Box>
              )}

              {/* Custom API Coming Soon (only for non-Moltbot, form step) */}
              {!isMoltbot && step === 'form' && (
                <Flex
                  bg="rgba(0,71,125,0.04)"
                  borderRadius="16px"
                  p={6}
                  direction="column"
                  align="center"
                  gap={3}
                  border="1px solid"
                  borderColor="rgba(0,71,125,0.12)"
                >
                  <Flex
                    w="48px"
                    h="48px"
                    borderRadius="14px"
                    bg="rgba(0,71,125,0.1)"
                    align="center"
                    justify="center"
                  >
                    <RiKeyLine size={24} color="#00477D" />
                  </Flex>
                  <Text fontSize="16px" fontWeight={700} color="#1a1a2e">
                    Coming Soon
                  </Text>
                  <Text fontSize="14px" color="#64748b" textAlign="center" maxW="360px" lineHeight="1.6">
                    Custom API agent support is under development. For now, use the Moltbot / OpenClaw option to connect a self-hosted agent.
                  </Text>
                </Flex>
              )}
            </Stack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>

          {/* Footer */}
          <Flex
            px={6}
            py={5}
            bg="#f8fafc"
            borderTop="1px solid"
            borderColor="#e2e8f0"
            gap={3}
            justify="flex-end"
            flexShrink={0}
          >
            {!showResult && mode === 'automatic' ? (
              <>
                <Box
                  as="button"
                  px={5}
                  py={2.5}
                  borderRadius="12px"
                  fontSize="14px"
                  fontWeight={600}
                  color="#64748b"
                  bg="white"
                  border="1px solid"
                  borderColor="#e2e8f0"
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{ bg: '#f1f5f9', borderColor: '#cbd5e1', color: '#475569' }}
                  _active={{ transform: 'scale(0.98)' }}
                  onClick={handleClose}
                >
                  Cancel
                </Box>
                <Box
                  as="button"
                  px={6}
                  py={2.5}
                  borderRadius="12px"
                  fontSize="14px"
                  fontWeight={600}
                  color="white"
                  bgGradient="linear(135deg, #ff4d4d 0%, #00e5cc 100%)"
                  cursor={(loading || enrollmentLoading || !canCreateAgent || !formData.name.trim()) ? 'not-allowed' : 'pointer'}
                  opacity={(loading || enrollmentLoading || !canCreateAgent || !formData.name.trim()) ? 0.6 : 1}
                  transition="all 0.2s"
                  boxShadow="0 2px 8px rgba(230,57,70,0.3)"
                  _hover={!(loading || enrollmentLoading) && canCreateAgent && formData.name.trim() ? {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(230,57,70,0.4)',
                  } : {}}
                  _active={!(loading || enrollmentLoading) && canCreateAgent && formData.name.trim() ? { transform: 'scale(0.98)' } : {}}
                  onClick={!(loading || enrollmentLoading) && canCreateAgent && formData.name.trim() ? handleGenerateAutomatic : undefined}
                >
                  {(loading || enrollmentLoading) ? 'Generating...' : 'Generate setup prompt'}
                </Box>
              </>
            ) : !showResult ? (
              <>
                <Box
                  as="button"
                  px={5}
                  py={2.5}
                  borderRadius="12px"
                  fontSize="14px"
                  fontWeight={600}
                  color="#64748b"
                  bg="white"
                  border="1px solid"
                  borderColor="#e2e8f0"
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{ bg: '#f1f5f9', borderColor: '#cbd5e1', color: '#475569' }}
                  _active={{ transform: 'scale(0.98)' }}
                  onClick={handleClose}
                >
                  Cancel
                </Box>
                <Box
                  as="button"
                  px={6}
                  py={2.5}
                  borderRadius="12px"
                  fontSize="14px"
                  fontWeight={600}
                  color="white"
                  bgGradient={`linear(135deg, ${gradientStart} 0%, ${gradientEnd} 100%)`}
                  cursor={(loading || enrollmentLoading || !canSubmit) ? 'not-allowed' : 'pointer'}
                  opacity={(loading || enrollmentLoading || !canSubmit) ? 0.6 : 1}
                  transition="all 0.2s"
                  boxShadow={`0 2px 8px ${accentColor}50`}
                  _hover={!(loading || enrollmentLoading) && canSubmit ? {
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${accentColor}60`,
                  } : {}}
                  _active={!(loading || enrollmentLoading) && canSubmit ? { transform: 'scale(0.98)' } : {}}
                  onClick={!(loading || enrollmentLoading) && canSubmit ? handleSubmit : undefined}
                >
                  {(loading || enrollmentLoading) ? 'Creating...' : isMoltbot ? 'Add Moltbot' : 'Add Agent'}
                </Box>
              </>
            ) : (
              <>
                <Text fontSize="13px" color="#64748b" alignSelf="center" mr="auto">
                  {isConnectorOnline
                    ? 'Your agent is connected and ready!'
                    : 'You can close this and connect later'}
                </Text>
                <Box
                  as="button"
                  px={6}
                  py={2.5}
                  borderRadius="12px"
                  fontSize="14px"
                  fontWeight={600}
                  color="white"
                  bg={isConnectorOnline ? '#22c55e' : '#64748b'}
                  cursor="pointer"
                  transition="all 0.2s"
                  boxShadow={isConnectorOnline ? '0 2px 8px rgba(34,197,94,0.3)' : 'none'}
                  _hover={{
                    transform: 'translateY(-1px)',
                    boxShadow: isConnectorOnline ? '0 4px 12px rgba(34,197,94,0.4)' : '0 4px 12px rgba(100,116,139,0.3)',
                  }}
                  _active={{ transform: 'scale(0.98)' }}
                  onClick={handleClose}
                >
                  {isConnectorOnline ? 'Done' : 'Close'}
                </Box>
              </>
            )}
          </Flex>
        </Box>
      </ModalContent>
    </Modal>
  );
};
